import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';
import { CardTile, copyLink, slotOrder, formOf } from './ClashDecks';

const pct = (x) => (x == null ? '—' : `${(x * 100).toFixed(1)}%`);
const pts = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}`);
const times = (x) => (x == null ? 'new' : `×${x.toFixed(x >= 10 ? 0 : 1)}`);
const nameOf = (cards, id) => cards[String(id)]?.name || `#${id}`;

function Rise({ rise, rising }) {
  const cls = rise == null ? 'flat' : rise >= rising ? 'up' : rise < 1 / rising ? 'down' : 'flat';
  return <span className={`trend ${cls}`}>{rise == null ? 'new' : `${times(rise)} play`}</span>;
}

function TrendDeck({ deck, data }) {
  const order = slotOrder(deck);
  return (
    <div className="dk">
      <div className="dk-head">
        <div>
          <div className="dk-name">
            {deck.archetype}
            {deck.hot && <span className="pill-chip lime tr-hot">Rising + winning</span>}
          </div>
          <div className="pt">
            {deck.elixir != null ? `${deck.elixir} avg elixir · ` : ''}{deck.n.toLocaleString()} games this week
            {' · '}<Rise rise={deck.rise} rising={data.thresholds.rising} />
          </div>
        </div>
        <div className="dk-wr">
          <div className="v">{pct(deck.adj_win_rate)}</div>
          <div className="l">win rate</div>
        </div>
      </div>
      <div className="dk-grid">
        {order.map((c) => <CardTile key={c} id={c} card={data.cards[String(c)]} form={formOf(deck, c)} />)}
      </div>
      <div className="dk-foot">
        <span className="pt">{deck.prior_n.toLocaleString()} games in the weeks before</span>
        <a className="dk-copy" href={copyLink(order)} target="_blank" rel="noopener noreferrer">Copy to game ↗</a>
      </div>
    </div>
  );
}

function PairArt({ cards, ids }) {
  return (
    <div className="tr-pair">
      {ids.map((c) => <CardTile key={c} id={c} card={cards[String(c)]} />)}
    </div>
  );
}

/* What's breaking the meta: decks whose play rate is climbing while they keep
   winning, and the card pairs that win more together than either card does
   on its own. Built weekly from tracked battles. */
function ClashTrending() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [risingOnly, setRisingOnly] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [allPairs, setAllPairs] = useState(false);
  usePageMeta({
    title: 'Trending Clash Royale Decks and Card Synergies',
    description: 'The Clash Royale decks rising fastest this week, and the card pairs that win more together than apart, measured from tracked battles and refreshed weekly.',
  });

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setData(null);
    api.getClashTrending()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [nonce]);

  const pairs = useMemo(() => {
    if (!data) return [];
    const rising = data.thresholds.rising;
    return data.pairs.filter((p) => p.clear && (!risingOnly || (p.rise != null && p.rise >= rising)));
  }, [data, risingOnly]);

  const hotDeck = data?.decks.find((d) => d.hot) || data?.decks[0];
  const topPair = data?.pairs.find((p) => p.clear);
  const decks = data ? (showAll ? data.decks : data.decks.slice(0, 12)) : [];

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Clash Royale · updated weekly</div>
            <h1>Trending Decks and Card Synergies</h1>
            <div className="sub">
              Decks that are catching on and still winning, and the card pairs that win more together than
              either card does alone: the combos most likely to be overpowered right now.
              {data ? ` From ${(data.games.recent + data.games.prior).toLocaleString()} decks played over the last ${data.window_days} days.` : ''}
            </div>
          </div>
        </div>

        {error ? (
          <ErrorMessage message={error} onRetry={() => setNonce((n) => n + 1)} />
        ) : !data ? (
          <SkeletonRows rows={8} />
        ) : (
          <>
            <div className="stat-ribbon">
              <div className="srib"><div className="v">{hotDeck ? hotDeck.archetype : '—'}</div><div className="l">Hottest deck</div></div>
              <div className="srib"><div className="v">{hotDeck ? times(hotDeck.rise) : '—'}</div><div className="l">Its play rate vs before</div></div>
              <div className="srib">
                <div className="v">{topPair ? `${nameOf(data.cards, topPair.cards[0])} + ${nameOf(data.cards, topPair.cards[1])}` : '—'}</div>
                <div className="l">Strongest synergy</div>
              </div>
              <div className="srib"><div className="v">{data.generated_at.slice(0, 10)}</div><div className="l">Last refreshed</div></div>
            </div>

            <div className="subhead">Rising and winning</div>
            <div className="sub dk-sub">
              Decks played more this week than in the {data.window_days - data.recent_days} days before, that also win
              more than they lose once card levels are taken out. Ranked by how fast they're climbing times how much
              they win. A deck needs {data.thresholds.min_recent_deck}+ games this week to show.
            </div>
            {decks.length === 0 ? (
              <div className="empty-state">No deck is clearly rising this week.</div>
            ) : (
              <div className="dk-list">
                {decks.map((d) => <TrendDeck key={d.cards.join('-')} deck={d} data={data} />)}
              </div>
            )}
            {!showAll && data.decks.length > decks.length && (
              <button className="retry-btn tr-more" onClick={() => setShowAll(true)}>
                Show all {data.decks.length} rising decks
              </button>
            )}

            <div className="subhead">Card pairs that win more together</div>
            <div className="sub dk-sub">
              <b>Synergy</b> is how many points the pair adds on top of what each card wins without the other. Zero
              means the two cards are only as good as they are apart; a big number means the combination does
              something neither card does alone.
            </div>
            <div className="sc-filters">
              <button className={`sc-chip${!risingOnly ? ' on' : ''}`} onClick={() => setRisingOnly(false)}>All clear synergies</button>
              <button className={`sc-chip${risingOnly ? ' on' : ''}`} onClick={() => setRisingOnly(true)}>Only pairs gaining play</button>
            </div>
            <div className="tablecard">
              <div className="table-scroll">
                <table className="meta">
                  <thead>
                    <tr>
                      <th>Pair</th>
                      <th title="Points the pair wins above what each card wins without the other">Synergy</th>
                      <th title="Adjusted win rate of decks running both cards">Together</th>
                      <th title="Adjusted win rate of decks with the first card but not the second">1st alone</th>
                      <th title="Adjusted win rate of decks with the second card but not the first">2nd alone</th>
                      <th title="Play rate of the pair this week against the weeks before">Trend</th>
                      <th title="Decks with both cards over the whole window">Games</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pairs.length === 0 ? (
                      <tr><td colSpan={7}><div className="empty-state">No pair clears the bar with this filter.</div></td></tr>
                    ) : (allPairs ? pairs : pairs.slice(0, 25)).map((p) => (
                      <tr key={p.cards.join('-')}>
                        <td style={{ minWidth: 220 }}>
                          <div className="pcell">
                            <PairArt cards={data.cards} ids={p.cards} />
                            <div>
                              <div className="pn" style={{ whiteSpace: 'nowrap' }}>
                                {nameOf(data.cards, p.cards[0])} + {nameOf(data.cards, p.cards[1])}
                              </div>
                              {p.hot && <div className="pt"><span className="pill-chip lime">Rising + winning</span></div>}
                            </div>
                          </div>
                        </td>
                        <td><span className="trend up">{pts(p.synergy)}</span></td>
                        <td><span className="num-cell">{pct(p.together)}</span></td>
                        <td><span className="num-cell">{pct(p.a_without_b)}</span></td>
                        <td><span className="num-cell">{pct(p.b_without_a)}</span></td>
                        <td><Rise rise={p.rise} rising={data.thresholds.rising} /></td>
                        <td><span className="num-cell">{p.n.toLocaleString()}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {!allPairs && pairs.length > 25 && (
              <button className="retry-btn tr-more tr-more-pairs" onClick={() => setAllPairs(true)}>
                Show all {pairs.length} pairs
              </button>
            )}

            {data.card_trends.length > 0 && (
              <>
                <div className="subhead">Cards gaining play</div>
                <div className="trend-strip">
                  {data.card_trends.slice(0, 12).map((c) => (
                    <div key={c.id} className="trend-card">
                      <div className="tr-card-art"><CardTile id={c.id} card={data.cards[String(c.id)]} /></div>
                      <div>
                        <div className="nm">{nameOf(data.cards, c.id)}</div>
                        <div className="meta"><Rise rise={c.rise} rising={data.thresholds.rising} /> · {pct(c.adj_win_rate)} win</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="tip">
              <b>How this works.</b> The Clash Royale API doesn't list decks, so we track Trophy Road, Path of Legends
              and war battles and compare this week ({data.recent_days} days to the newest battle) with the weeks before.
              Win rates are adjusted for card levels, so a deck doesn't look strong just because its players are
              better levelled. Each synergy compares four groups of decks: both cards, each card without the other,
              and neither. A pair is only listed once the result clears {data.thresholds.synergy_z} standard errors and
              it has {data.thresholds.min_pair_games.toLocaleString()}+ games together. Players pick their decks, so a
              strong synergy can partly reflect who plays it, and a pair inside a single dominant deck will share that
              deck's edge.
            </div>
            <div className="tip">
              Want one built on your cards? <Link to="/clash/decks">Find decks for your levels →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ClashTrending;
